import { AudioEditor } from './AudioEditor';
import { LedPreview } from './LedPreview';
import { DEFAULT_LED_SETTINGS, getLedSettings, normalizeLedSettings, saveLedSettings, type LedSettings } from '../assets/LedSettings';
import type { AudioSystem } from './AudioSystem';
import type { VfxSystem } from '../assets/Vfx';
import { DEFAULT_AD_TEXTS, MAX_AD_TEXT_LENGTH, setAdvertisingDesign, type AdvertisingDesign } from '../assets/textures';
import { DEFAULT_IMPACT_WORDS, IMPACT_WORDS_STORAGE_KEY, loadImpactWords, normalizeImpactWords, type ImpactWordKey } from './ImpactWords';
import { CONTROL_ACTIONS, CONTROL_ACTION_LABELS, CONTROL_SETTINGS_STORAGE_KEY, GAMEPAD_ACTIONS, GAMEPAD_BUTTON_LABELS, loadControlSettings, normalizeControlSettings, type ControlSettings } from '../core/ControlSettings';
import { assignGamepads, padButton, readGamepads } from '../core/Gamepads';
import { COMBAT_CHARACTERS, DEFAULT_COMBAT_TUNING, getCombatTuning, saveCombatTuning, type CombatTuning } from '../game/CombatTuning';
import { ATTACKS } from '../game/combat';
import { WEAPONS } from '../game/weapons';

const AD_STORAGE_KEY = 'futbol-fighters-advertising-v1';
const FPS_STORAGE_KEY = 'futbol-fighters-show-fps-v1';
type SavedAds = AdvertisingDesign[];
const defaultAds = (): SavedAds => DEFAULT_AD_TEXTS.map(() => ({ mode: 'default' }));

function loadAds(): SavedAds {
  try {
    const value = JSON.parse(localStorage.getItem(AD_STORAGE_KEY) ?? 'null');
    if (Array.isArray(value) && value.length === DEFAULT_AD_TEXTS.length) return value.map(ad => {
      if (ad?.mode === 'text' && typeof ad.text === 'string') return { mode: 'text', text: Array.from(ad.text).slice(0, MAX_AD_TEXT_LENGTH).join('') };
      if (ad?.mode === 'image' && typeof ad.image === 'string' && ad.image.length <= 1_000_000 && /^data:image\/jpeg;base64,[A-Za-z0-9+/=]+$/.test(ad.image)) return { mode: 'image', image: ad.image };
      return { mode: 'default' };
    });
  } catch { /* Use original boards when storage is unavailable or malformed. */ }
  return defaultAds();
}

export class OptionsPanel {
  private readonly dialog = document.querySelector<HTMLDialogElement>('#options-dialog')!;
  private readonly form = this.dialog.querySelector<HTMLFormElement>('form')!;
  private readonly button = document.querySelector<HTMLButtonElement>('#btn-options')!;
  private readonly editorDialog = document.querySelector<HTMLDialogElement>('#editor-dialog')!;
  private readonly editorButton = document.querySelector<HTMLButtonElement>('#btn-editor')!;
  private readonly editorMenuButton = document.querySelector<HTMLButtonElement>('#menu-editor')!;
  private readonly authForm = this.editorDialog.querySelector<HTMLFormElement>('#editor-auth-form')!;
  private readonly editorForm = this.editorDialog.querySelector<HTMLFormElement>('#editor-form')!;
  private readonly editorToken = this.editorDialog.querySelector<HTMLInputElement>('#editor-token')!;
  private readonly authStatus = this.editorDialog.querySelector<HTMLElement>('#editor-auth-status')!;
  private readonly editorCancel = this.editorDialog.querySelector<HTMLButtonElement>('#editor-cancel')!;
  private readonly editorAuthCancel = this.editorDialog.querySelector<HTMLButtonElement>('#editor-auth-cancel')!;
  private readonly reset = this.editorDialog.querySelector<HTMLButtonElement>('#editor-reset')!;
  private readonly cancel = this.dialog.querySelector<HTMLButtonElement>('#options-cancel')!;
  private readonly adSlots = this.editorDialog.querySelector<HTMLElement>('#court-signs')!;
  private readonly adTemplate = this.editorDialog.querySelector<HTMLTemplateElement>('#court-sign-template')!;
  private readonly adStatus = this.editorDialog.querySelector<HTMLElement>('#ad-status')!;
  private readonly ledControls = this.editorDialog.querySelector<HTMLElement>('#court-led-controls')!;
  private ledPreview: LedPreview | null = null;
  private readonly attackDamageControls = this.editorDialog.querySelector<HTMLElement>('#attack-damage-controls')!;
  private readonly characterHpControls = this.editorDialog.querySelector<HTMLElement>('#character-hp-controls')!;
  private readonly combatStatus = this.editorDialog.querySelector<HTMLElement>('#combat-status')!;
  private readonly sfxVolume = this.editorDialog.querySelector<HTMLInputElement>('#sfx-volume')!;
  private readonly musicVolume = this.editorDialog.querySelector<HTMLInputElement>('#music-volume')!;
  private readonly showFps = this.dialog.querySelector<HTMLInputElement>('#show-fps')!;
  private readonly controlSlots = this.dialog.querySelector<HTMLElement>('#player-control-slots')!;
  private readonly controlTemplate = this.dialog.querySelector<HTMLTemplateElement>('#player-control-template')!;
  private readonly controlStatus = this.dialog.querySelector<HTMLElement>('#control-status')!;
  private readonly events = new AbortController();
  private opened: HTMLDialogElement | null = null;
  private readonly audioEditor: AudioEditor;
  private ads = loadAds();
  private editImages: string[] = [];
  private imageJobs: Promise<void>[] = [];
  private imageJobsVersion = Array(DEFAULT_AD_TEXTS.length).fill(0) as number[];
  private controls = loadControlSettings();
  private captureTarget: HTMLOutputElement | null = null;
  private captureButton: HTMLButtonElement | null = null;
  private padCapture: HTMLSelectElement | null = null;
  private padCaptureArmed = false;
  private padPollAt = 0;

  constructor(private readonly vfx: VfxSystem, audio: AudioSystem, private readonly onOpenChange: (open: boolean) => void, private readonly onFpsChange: (visible: boolean) => void, private readonly onControlChange: (settings: ControlSettings) => void, private readonly onCombatTuningChange: (settings: CombatTuning) => void) {
    this.audioEditor = new AudioEditor(audio, () => this.editorToken.value);
    vfx.setImpactWords(loadImpactWords());
    onControlChange(this.controls);
    onCombatTuningChange(getCombatTuning());
    try { this.showFps.checked = localStorage.getItem(FPS_STORAGE_KEY) === 'true'; } catch { /* Keep the counter off when storage is unavailable. */ }
    onFpsChange(this.showFps.checked);
    this.buildAdSlots();
    this.buildCombatControls();
    this.buildControlSlots();
    this.fill(this.vfx.impactWords);
    setAdvertisingDesign(this.ads);
    const options = { signal: this.events.signal };
    this.button.addEventListener('click', () => this.open(this.dialog), options);
    const openEditor = () => {
      this.authForm.hidden = false;
      this.editorForm.hidden = true;
      this.editorToken.value = '';
      this.authStatus.textContent = '';
      this.open(this.editorDialog);
    };
    this.editorButton.addEventListener('click', openEditor, options);
    this.editorMenuButton.addEventListener('click', openEditor, options);
    this.form.addEventListener('submit', async event => {
      event.preventDefault();
      const controls = this.readControlSettings();
      if (!controls) return;
      this.onFpsChange(this.showFps.checked);
      this.controls = controls;
      this.onControlChange(controls);
      try {
        localStorage.setItem(FPS_STORAGE_KEY, String(this.showFps.checked));
        localStorage.setItem(CONTROL_SETTINGS_STORAGE_KEY, JSON.stringify(controls));
      } catch { this.controlStatus.textContent = 'Se aplicó para esta sesión, pero el navegador no permitió guardarlo.'; }
      this.close();
    }, options);
    this.editorForm.addEventListener('submit', async event => {
      event.preventDefault();
      await Promise.all(this.imageJobs);
      const words = normalizeImpactWords(Object.fromEntries(new FormData(this.editorForm)));
      const designs = this.readAdSlots();
      const missingImage = designs.findIndex((ad, index) => ad.mode === 'image' && !this.editImages[index]);
      if (missingImage >= 0) {
        this.adStatus.textContent = `Selecciona una imagen para el cartel ${missingImage + 1}.`;
        this.adSlots.querySelectorAll<HTMLDetailsElement>('.court-sign')[missingImage].open = true;
        return;
      }
      const savedTuning = saveCombatTuning(this.readCombatTuning());
      this.onCombatTuningChange(savedTuning.settings);
      this.vfx.setImpactWords(words);
      this.ads = designs;
      setAdvertisingDesign(this.ads);
      const ledSaved = saveLedSettings(this.readLedSettings());
      try {
        localStorage.setItem(IMPACT_WORDS_STORAGE_KEY, JSON.stringify(words));
        localStorage.setItem(AD_STORAGE_KEY, JSON.stringify(this.ads));
        this.adStatus.textContent = '';
      } catch { this.adStatus.textContent = 'Mensajes y carteles: se aplicaron para esta sesión, pero el navegador no permitió guardarlos.'; }
      this.combatStatus.textContent = savedTuning.persisted ? '' : 'Daño, vida y volumen aplicados para esta sesión; el navegador no permitió guardarlos.';
      if (!ledSaved.persisted) {
        this.adStatus.textContent = 'Los efectos LED se aplicaron para esta sesión, pero el navegador no permitió guardarlos. Puedes cerrar con Cancelar.';
        this.adStatus.scrollIntoView({ block: 'nearest' });
        return;
      }
      this.close();
    }, options);
    this.editorForm.addEventListener('input', event => {
      const input = event.target;
      if (input instanceof HTMLInputElement && input.type === 'text') {
        const limit = input.matches('.court-sign-copy') ? MAX_AD_TEXT_LENGTH : 24;
        if (Array.from(input.value).length > limit) input.value = Array.from(input.value).slice(0, limit).join('');
      }
    }, options);
    this.adSlots.addEventListener('change', event => this.onAdChange(event), options);
    this.controlSlots.addEventListener('click', event => this.onControlClick(event), options);
    this.controlSlots.addEventListener('change', event => this.onControlUiChange(event), options);
    window.addEventListener('keydown', this.onCaptureKey, options);
    this.authForm.addEventListener('submit', event => { event.preventDefault(); void this.unlockEditor(); }, options);
    this.reset.addEventListener('click', () => this.fillEditor(DEFAULT_IMPACT_WORDS, defaultAds(), DEFAULT_COMBAT_TUNING, DEFAULT_LED_SETTINGS), options);
    this.ledControls.addEventListener('input', () => this.updateLedPreview(), options);
    this.editorForm.addEventListener('input', event => {
      const target = event.target;
      if (target === this.sfxVolume) this.editorDialog.querySelector<HTMLOutputElement>('#sfx-volume-value')!.value = `${this.sfxVolume.value}%`;
      if (target === this.musicVolume) this.editorDialog.querySelector<HTMLOutputElement>('#music-volume-value')!.value = `${this.musicVolume.value}%`;
    }, options);
    this.cancel.addEventListener('click', () => this.close(), options);
    this.editorCancel.addEventListener('click', () => this.close(), options);
    this.editorAuthCancel.addEventListener('click', () => this.close(), options);
    for (const dialog of [this.dialog, this.editorDialog]) {
      dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); }, options);
      dialog.addEventListener('close', () => { if (!dialog.open) this.finishClose(); }, options);
    }
  }

  get isOpen(): boolean { return this.opened !== null; }
  get isCapturingGamepad(): boolean { return this.padCapture !== null; }

  get controlSettings(): ControlSettings { return normalizeControlSettings(this.controls); }

  setPlayerControls(settings: ControlSettings): void {
    this.controls = normalizeControlSettings(settings);
    this.onControlChange(this.controls);
    try { localStorage.setItem(CONTROL_SETTINGS_STORAGE_KEY, JSON.stringify(this.controls)); } catch { /* Session controls still work. */ }
  }

  useGamepads(): void {
    const pads = readGamepads();
    if (this.controls.some(control => control.mode === 'gamepad')) return;
    const connected = pads.flatMap((pad, index) => pad?.connected ? [index] : []);
    connected.slice(0, 2).forEach((index, player) => {
      this.controls[player].mode = 'gamepad';
      this.controls[player].padIndex = index;
    });
    this.onControlChange(this.controls);
    try { localStorage.setItem(CONTROL_SETTINGS_STORAGE_KEY, JSON.stringify(this.controls)); } catch { /* Session controls still work. */ }
  }

  updateGamepads(): void {
    if (!this.opened) return;
    const pads = readGamepads();
    const slots = Array.from(this.controlSlots.querySelectorAll<HTMLElement>('.player-control'));
    const draft = normalizeControlSettings(slots.map((slot, player) => ({ ...this.controls[player], mode: slot.querySelector<HTMLSelectElement>('.player-control-mode')!.value, padIndex: Number(slot.querySelector<HTMLSelectElement>('.player-pad-device')!.value) })));
    const assigned = assignGamepads(draft, pads);
    if (this.padCapture) {
      const player = Number(this.padCapture.closest<HTMLElement>('.player-control')!.dataset.player);
      const pad = pads[assigned[player]];
      if (pad?.connected) {
        const button = pad.buttons.findIndex((_, index) => padButton(pad, index));
        if (button < 0) this.padCaptureArmed = true;
        else if (this.padCaptureArmed && button < GAMEPAD_BUTTON_LABELS.length) {
          this.padCapture.value = String(button);
          this.cancelKeyCapture();
        }
      }
    }
    if (performance.now() < this.padPollAt) return;
    this.padPollAt = performance.now() + 150;
    slots.forEach((slot, player) => {
      const select = slot.querySelector<HTMLSelectElement>('.player-pad-device')!;
      for (let index = 0; index < 16; index += 1) {
        const name = pads[index]?.connected ? `${index + 1}: ${pads[index]!.id || 'Mando conectado'}` : `${index + 1}: sin conectar`;
        if (select.options[index + 1].textContent !== name) select.options[index + 1].textContent = name;
      }
      const pad = pads[assigned[player]];
      const status = !navigator.getGamepads ? 'Este navegador no ofrece acceso a mandos.' : pad?.connected
        ? `Conectado: ${pad.id || 'Mando'} · ${pad.mapping === 'standard' ? 'estándar' : 'genérico: revisa ejes y botones'}`
        : 'Sin mando. Conéctalo y pulsa uno de sus botones.';
      const output = slot.querySelector<HTMLElement>('.player-pad-status')!;
      if (output.textContent !== status) output.textContent = status;
      slot.querySelector<HTMLElement>('.player-pad-monitor')!.textContent = pad?.connected
        ? `Ejes: ${pad.axes.map((value, index) => `${index}=${value.toFixed(2)}`).join(' · ')} | Botones: ${pad.buttons.flatMap((_, index) => padButton(pad, index) ? [index] : []).join(', ') || 'ninguno'}` : '';
    });
  }

  private buildAdSlots(): void {
    for (let index = 0; index < DEFAULT_AD_TEXTS.length; index += 1) {
      const fragment = this.adTemplate.content.cloneNode(true) as DocumentFragment;
      const slot = fragment.querySelector<HTMLElement>('.court-sign')!;
      slot.dataset.index = String(index);
      slot.querySelector('summary')!.textContent = `CARTEL ${index + 1} · ${DEFAULT_AD_TEXTS[index]}`;
      this.adSlots.append(fragment);
    }
  }

  private buildCombatControls(): void {
    for (const [key, attack] of Object.entries(ATTACKS)) this.addNumberControl(this.attackDamageControls, `attack-${key}`, attack.name, '0');
    for (const [key, weapon] of Object.entries(WEAPONS)) this.addNumberControl(this.attackDamageControls, `weapon-${key}`, weapon.name, '0');
    for (const [key, label] of [['slide', 'BARRIDA'], ['hammer', 'MARTILLO'], ['bomb', 'BOMBA']] as const) this.addNumberControl(this.attackDamageControls, `special-${key}`, label, '0');
    for (const character of COMBAT_CHARACTERS) this.addNumberControl(this.characterHpControls, `hp-${character.id}`, character.name, '1');
  }

  private addNumberControl(container: HTMLElement, key: string, label: string, min: string): void {
    const wrapper = document.createElement('label');
    wrapper.textContent = label;
    const input = document.createElement('input');
    input.type = 'number'; input.min = min; input.max = '1000'; input.step = '1'; input.dataset.tuning = key;
    input.setAttribute('aria-label', label);
    wrapper.append(input);
    container.append(wrapper);
  }

  private fillCombatControls(settings: CombatTuning): void {
    for (const [key, value] of Object.entries(settings.attackDamage)) this.editorForm.querySelector<HTMLInputElement>(`[data-tuning="attack-${key}"]`)!.value = String(value);
    for (const [key, value] of Object.entries(settings.weaponDamage)) this.editorForm.querySelector<HTMLInputElement>(`[data-tuning="weapon-${key}"]`)!.value = String(value);
    for (const [key, value] of Object.entries(settings.specialDamage)) this.editorForm.querySelector<HTMLInputElement>(`[data-tuning="special-${key}"]`)!.value = String(value);
    for (const [key, value] of Object.entries(settings.characterHp)) this.editorForm.querySelector<HTMLInputElement>(`[data-tuning="hp-${key}"]`)!.value = String(value);
    this.sfxVolume.value = String(settings.sfxVolume); this.musicVolume.value = String(settings.musicVolume);
    this.editorDialog.querySelector<HTMLOutputElement>('#sfx-volume-value')!.value = `${settings.sfxVolume}%`;
    this.editorDialog.querySelector<HTMLOutputElement>('#music-volume-value')!.value = `${settings.musicVolume}%`;
  }

  private readCombatTuning(): CombatTuning {
    const settings = getCombatTuning();
    for (const key of Object.keys(settings.attackDamage)) settings.attackDamage[key as keyof CombatTuning['attackDamage']] = Number(this.editorForm.querySelector<HTMLInputElement>(`[data-tuning="attack-${key}"]`)!.value);
    for (const key of Object.keys(settings.weaponDamage)) settings.weaponDamage[key as keyof CombatTuning['weaponDamage']] = Number(this.editorForm.querySelector<HTMLInputElement>(`[data-tuning="weapon-${key}"]`)!.value);
    for (const key of Object.keys(settings.specialDamage)) settings.specialDamage[key as keyof CombatTuning['specialDamage']] = Number(this.editorForm.querySelector<HTMLInputElement>(`[data-tuning="special-${key}"]`)!.value);
    for (const key of Object.keys(settings.characterHp)) settings.characterHp[key as keyof CombatTuning['characterHp']] = Number(this.editorForm.querySelector<HTMLInputElement>(`[data-tuning="hp-${key}"]`)!.value);
    settings.sfxVolume = Number(this.sfxVolume.value); settings.musicVolume = Number(this.musicVolume.value);
    return settings;
  }

  private buildControlSlots(): void {
    for (let player = 0; player < 2; player += 1) {
      const fragment = this.controlTemplate.content.cloneNode(true) as DocumentFragment;
      const slot = fragment.querySelector<HTMLElement>('.player-control')!;
      slot.dataset.player = String(player);
      slot.querySelector('summary')!.textContent = player === 0 ? 'JUGADOR 1 · AZUL' : 'JUGADOR 2 · ROJO';
      const keyBindings = slot.querySelector<HTMLElement>('.player-key-bindings')!;
      for (const action of CONTROL_ACTIONS) {
        const row = document.createElement('div');
        row.className = 'bind-row';
        const label = document.createElement('span');
        label.textContent = CONTROL_ACTION_LABELS[action];
        const key = document.createElement('output');
        key.className = 'key-binding';
        key.dataset.action = action;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'bind-key';
        button.textContent = 'Cambiar';
        button.setAttribute('aria-label', `Cambiar tecla: ${CONTROL_ACTION_LABELS[action]}`);
        row.append(label, key, button);
        keyBindings.append(row);
      }
      const padBindings = slot.querySelector<HTMLElement>('.player-pad-bindings')!;
      for (const selector of ['.player-pad-device', '.player-pad-axis-x', '.player-pad-axis-y']) {
        const select = slot.querySelector<HTMLSelectElement>(selector)!;
        for (let index = 0; index < 16; index += 1) select.add(new Option(selector === '.player-pad-device' ? `${index + 1}: sin conectar` : `Eje ${index}`, String(index)));
      }
      for (const action of GAMEPAD_ACTIONS) {
        const label = document.createElement('label');
        label.className = 'bind-row';
        label.append(document.createTextNode(CONTROL_ACTION_LABELS[action]));
        const select = document.createElement('select');
        select.className = 'gamepad-binding';
        select.dataset.action = action;
        select.setAttribute('aria-label', `${CONTROL_ACTION_LABELS[action]} en el mando`);
        GAMEPAD_BUTTON_LABELS.forEach((name, index) => {
          const option = document.createElement('option');
          option.value = String(index);
          option.textContent = name;
          select.append(option);
        });
        label.append(select);
        const detect = document.createElement('button');
        detect.type = 'button';
        detect.className = 'bind-pad';
        detect.textContent = 'Detectar';
        detect.setAttribute('aria-label', `Detectar botón: ${CONTROL_ACTION_LABELS[action]}`);
        label.append(detect);
        padBindings.append(label);
      }
      this.controlSlots.append(fragment);
    }
  }

  private fillControls(): void {
    this.controlStatus.textContent = '';
    this.controlSlots.querySelectorAll<HTMLElement>('.player-control').forEach((slot, player) => {
      const settings = this.controls[player];
      slot.querySelector<HTMLSelectElement>('.player-control-mode')!.value = settings.mode;
      slot.querySelector<HTMLSelectElement>('.player-pad-device')!.value = String(settings.padIndex);
      slot.querySelector<HTMLSelectElement>('.player-pad-axis-x')!.value = String(settings.axisX);
      slot.querySelector<HTMLSelectElement>('.player-pad-axis-y')!.value = String(settings.axisY);
      slot.querySelector<HTMLInputElement>('.player-pad-invert-x')!.checked = settings.invertX;
      slot.querySelector<HTMLInputElement>('.player-pad-invert-y')!.checked = settings.invertY;
      slot.querySelector<HTMLInputElement>('.player-pad-deadzone')!.value = String(Math.round(settings.deadzone * 100));
      for (const action of CONTROL_ACTIONS) {
        const key = slot.querySelector<HTMLOutputElement>(`.key-binding[data-action="${action}"]`)!;
        key.dataset.code = settings.keys[action];
        key.textContent = this.formatKey(settings.keys[action]);
      }
      for (const action of GAMEPAD_ACTIONS) slot.querySelector<HTMLSelectElement>(`.gamepad-binding[data-action="${action}"]`)!.value = String(settings.buttons[action]);
      this.updateControlMode(slot, settings.mode);
    });
  }

  private updateControlMode(slot: HTMLElement, mode: string): void {
    slot.querySelector<HTMLElement>('.player-key-bindings')!.hidden = mode !== 'keyboard';
    slot.querySelector<HTMLElement>('.player-pad-bindings')!.hidden = mode !== 'gamepad';
    slot.querySelector<HTMLElement>('.player-touch-note')!.hidden = mode !== 'touch';
  }

  private onControlUiChange(event: Event): void {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement) || !target.matches('.player-control-mode')) return;
    const slot = target.closest<HTMLElement>('.player-control');
    if (slot) this.updateControlMode(slot, target.value);
  }

  private onControlClick(event: Event): void {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const padButton = target.closest<HTMLButtonElement>('.bind-pad');
    if (padButton) {
      this.cancelKeyCapture();
      this.padCapture = padButton.closest('.bind-row')!.querySelector<HTMLSelectElement>('select');
      this.padCaptureArmed = false;
      this.captureButton = padButton;
      padButton.textContent = 'Pulsa botón…';
      this.controlStatus.textContent = 'Suelta los botones y pulsa el que quieras asignar. Escape cancela.';
      return;
    }
    const button = target.closest<HTMLButtonElement>('.bind-key');
    if (!button) return;
    this.cancelKeyCapture();
    this.captureTarget = button.closest('.bind-row')?.querySelector<HTMLOutputElement>('.key-binding') ?? null;
    this.captureButton = button;
    button.textContent = 'Pulsa tecla…';
    this.controlStatus.textContent = 'Pulsa la nueva tecla para asignarla. Escape cancela.';
  }

  private readonly onCaptureKey = (event: KeyboardEvent): void => {
    if (!this.captureTarget && !this.padCapture) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (event.code === 'Escape') {
      this.cancelKeyCapture();
      return;
    }
    if (!this.captureTarget) return;
    this.captureTarget.dataset.code = event.code;
    this.captureTarget.textContent = this.formatKey(event.code);
    this.cancelKeyCapture();
  };

  private cancelKeyCapture(): void {
    if (this.captureButton) this.captureButton.textContent = this.padCapture ? 'Detectar' : 'Cambiar';
    this.padCapture = null;
    this.captureButton = null;
    this.captureTarget = null;
    this.controlStatus.textContent = '';
  }

  private formatKey(code: string): string {
    return ({ Space: 'Espacio', ArrowUp: '↑', ArrowDown: '↓', ArrowLeft: '←', ArrowRight: '→' } as Record<string, string>)[code]
      ?? code.replace(/^Key|^Digit|^Numpad/, '');
  }

  private readControlSettings(): ControlSettings | null {
    const values = Array.from(this.controlSlots.querySelectorAll<HTMLElement>('.player-control'), (slot, player) => {
      const mode = slot.querySelector<HTMLSelectElement>('.player-control-mode')!.value;
      const keys = Object.fromEntries(CONTROL_ACTIONS.map(action => [action, slot.querySelector<HTMLOutputElement>(`.key-binding[data-action="${action}"]`)!.dataset.code ?? ''])) as ControlSettings[number]['keys'];
      const buttons = Object.fromEntries(GAMEPAD_ACTIONS.map(action => [action, Number(slot.querySelector<HTMLSelectElement>(`.gamepad-binding[data-action="${action}"]`)!.value)])) as ControlSettings[number]['buttons'];
      const assigned = mode === 'keyboard' ? Object.values(keys) : mode === 'gamepad' ? Object.values(buttons).map(String) : [];
      if (new Set(assigned).size !== assigned.length) {
        this.controlStatus.textContent = `Jugador ${player + 1}: asigna una tecla o botón distinto a cada acción.`;
        return null;
      }
      const padIndex = Number(slot.querySelector<HTMLSelectElement>('.player-pad-device')!.value);
      const axisX = Number(slot.querySelector<HTMLSelectElement>('.player-pad-axis-x')!.value);
      const axisY = Number(slot.querySelector<HTMLSelectElement>('.player-pad-axis-y')!.value);
      if (mode === 'gamepad' && axisX === axisY) {
        this.controlStatus.textContent = `Jugador ${player + 1}: usa un eje distinto para cada dirección.`;
        return null;
      }
      return { mode, keys, buttons, padIndex, axisX, axisY,
        invertX: slot.querySelector<HTMLInputElement>('.player-pad-invert-x')!.checked,
        invertY: slot.querySelector<HTMLInputElement>('.player-pad-invert-y')!.checked,
        deadzone: Number(slot.querySelector<HTMLInputElement>('.player-pad-deadzone')!.value) / 100 };
    });
    if (values[0]?.mode === 'gamepad' && values[1]?.mode === 'gamepad' && values[0].padIndex >= 0 && values[0].padIndex === values[1].padIndex) {
      this.controlStatus.textContent = 'Elige un mando diferente para cada jugador.';
      return null;
    }
    return values.some(value => value === null) ? null : normalizeControlSettings(values);
  }

  private open(dialog: HTMLDialogElement): void {
    if (this.opened) this.close();
    if (dialog === this.editorDialog) this.editorForm.hidden = true;
    else this.fillControls();
    dialog.showModal();
    this.opened = dialog;
    this.onOpenChange(true);
  }

  private async unlockEditor(): Promise<void> {
    const token = this.editorToken.value.trim();
    if (!token) return;
    const submit = this.authForm.querySelector<HTMLButtonElement>('button[type="submit"]')!;
    submit.disabled = true;
    this.authStatus.textContent = 'Verificando clave con Oracle…';
    try {
      const response = await fetch(`${import.meta.env.BASE_URL}api/editor/auth`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error(response.status === 401 ? 'Clave de creador incorrecta.' : 'Oracle no pudo validar la clave.');
      this.fillEditor(this.vfx.impactWords);
      this.authForm.hidden = true;
      this.editorForm.hidden = false;
      try {
        this.ledPreview = new LedPreview(this.editorDialog.querySelector<HTMLCanvasElement>('#court-led-preview')!, this.readLedSettings());
        this.editorDialog.querySelector<HTMLElement>('#court-led-preview-status')!.textContent = '';
      } catch {
        this.editorDialog.querySelector<HTMLElement>('#court-led-preview-status')!.textContent = 'No se pudo iniciar la vista previa. Puedes guardar y comprobar los efectos en la cancha.';
      }
      this.editorForm.querySelector<HTMLElement>('h2')?.focus();
    } catch (error) {
      this.authStatus.textContent = error instanceof Error ? error.message : 'No se pudo validar la clave.';
      this.editorToken.focus();
    } finally { submit.disabled = false; }
  }

  private fill(words: Readonly<Record<ImpactWordKey, string>>, ads: SavedAds = this.ads): void {
    this.fillControls();
    this.fillEditor(words, ads);
  }

  private fillEditor(words: Readonly<Record<ImpactWordKey, string>>, ads: SavedAds = this.ads, tuning = getCombatTuning(), led: Readonly<LedSettings> = getLedSettings()): void {
    for (const [key, value] of Object.entries(led)) {
      const control = this.ledControls.querySelector<HTMLInputElement | HTMLSelectElement>(`[name="led-${key}"]`)!;
      if (control instanceof HTMLInputElement && control.type === 'checkbox') control.checked = value as boolean;
      else control.value = String(value);
    }
    this.updateLedPreview();
    for (const key of Object.keys(DEFAULT_IMPACT_WORDS) as ImpactWordKey[]) (this.editorForm.elements.namedItem(key) as HTMLInputElement).value = words[key];
    this.editImages = DEFAULT_AD_TEXTS.map((_, index) => ads[index]?.mode === 'image' ? (ads[index] as Extract<AdvertisingDesign, { mode: 'image' }>).image : '');
    this.imageJobsVersion = this.imageJobsVersion.map(version => version + 1);
    this.imageJobs = [];
    this.adStatus.textContent = '';
    this.combatStatus.textContent = '';
    this.fillCombatControls(tuning);
    this.adSlots.querySelectorAll<HTMLElement>('.court-sign').forEach((slot, index) => {
      const ad = ads[index] ?? { mode: 'default' };
      const mode = slot.querySelector<HTMLSelectElement>('.court-sign-mode')!;
      const text = slot.querySelector<HTMLInputElement>('.court-sign-copy')!;
      const imageFile = slot.querySelector<HTMLInputElement>('.court-sign-image-file')!;
      const textFile = slot.querySelector<HTMLInputElement>('.court-sign-text-file')!;
      const preview = slot.querySelector<HTMLImageElement>('.court-sign-preview')!;
      mode.value = ad.mode;
      text.value = ad.mode === 'text' ? ad.text : '';
      imageFile.value = '';
      textFile.value = '';
      preview.src = this.editImages[index];
      this.updateAdSlot(slot, index);
    });
  }

  private updateAdSlot(slot: HTMLElement, index: number): void {
    const mode = slot.querySelector<HTMLSelectElement>('.court-sign-mode')!.value;
    slot.querySelector<HTMLElement>('.court-sign-text')!.hidden = mode !== 'text';
    slot.querySelector<HTMLElement>('.court-sign-image')!.hidden = mode !== 'image';
    const preview = slot.querySelector<HTMLImageElement>('.court-sign-preview')!;
    preview.hidden = mode !== 'image' || !this.editImages[index];
  }

  private readLedSettings(): LedSettings {
    return normalizeLedSettings(Object.fromEntries(Object.entries(DEFAULT_LED_SETTINGS).map(([key, fallback]) => {
      const control = this.ledControls.querySelector<HTMLInputElement | HTMLSelectElement>(`[name="led-${key}"]`)!;
      const value = control instanceof HTMLInputElement && control.type === 'checkbox' ? control.checked : typeof fallback === 'number' ? Number(control.value) : control.value;
      return [key, value];
    })));
  }

  private updateLedPreview(): void {
    const settings = this.readLedSettings();
    this.editorDialog.querySelector<HTMLOutputElement>('#court-led-blink-value')!.value = settings.blink === 0 ? 'Apagado' : `${settings.blink}%`;
    this.ledPreview?.configure(settings);
  }

  private onAdChange(event: Event): void {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
    const slot = target.closest<HTMLElement>('.court-sign');
    if (!slot) return;
    const index = Number(slot.dataset.index);
    const mode = slot.querySelector<HTMLSelectElement>('.court-sign-mode')!;
    if (target === mode) this.updateAdSlot(slot, index);
    if (!(target instanceof HTMLInputElement) || target.type !== 'file') return;
    const file = target.files?.[0];
    if (!file) return;
    if (target.matches('.court-sign-text-file')) {
      if (file.size > 64_000) {
        this.adStatus.textContent = 'El archivo de texto debe pesar menos de 64 KB.';
        return;
      }
      const version = ++this.imageJobsVersion[index];
      this.imageJobs[index] = file.text().then(contents => {
        if (version !== this.imageJobsVersion[index]) return;
        slot.querySelector<HTMLInputElement>('.court-sign-copy')!.value = Array.from(contents.replace(/\s+/gu, ' ').trim()).slice(0, MAX_AD_TEXT_LENGTH).join('');
        mode.value = 'text';
        this.updateAdSlot(slot, index);
      }).catch(() => {
        if (version === this.imageJobsVersion[index]) this.adStatus.textContent = 'No se pudo leer ese archivo de texto.';
      });
      return;
    }
    const version = ++this.imageJobsVersion[index];
    this.adStatus.textContent = '';
    this.imageJobs[index] = this.prepareImage(file).then(data => {
      if (version !== this.imageJobsVersion[index]) return;
      this.editImages[index] = data;
      mode.value = 'image';
      slot.querySelector<HTMLImageElement>('.court-sign-preview')!.src = data;
      this.updateAdSlot(slot, index);
    }).catch(error => {
      if (version !== this.imageJobsVersion[index]) return;
      this.adStatus.textContent = error instanceof Error ? error.message : 'No se pudo leer esa imagen.';
    });
  }

  private readAdSlots(): SavedAds {
    return Array.from(this.adSlots.querySelectorAll<HTMLElement>('.court-sign'), (slot, index) => {
      const mode = slot.querySelector<HTMLSelectElement>('.court-sign-mode')!.value;
      if (mode === 'text') return { mode: 'text', text: Array.from(slot.querySelector<HTMLInputElement>('.court-sign-copy')!.value.trim()).slice(0, MAX_AD_TEXT_LENGTH).join('') || DEFAULT_AD_TEXTS[index] };
      return mode === 'image' ? { mode: 'image', image: this.editImages[index] } : { mode: 'default' };
    });
  }

  private async prepareImage(file: File): Promise<string> {
    if (!['image/png', 'image/jpeg', 'image/webp', 'image/gif'].includes(file.type) || file.size > 12_000_000) throw new Error('Usa una imagen PNG, JPG, WEBP o GIF de hasta 12 MB.');
    const url = URL.createObjectURL(file);
    try {
      const image = new Image();
      image.src = url;
      await image.decode();
      const canvas = document.createElement('canvas');
      canvas.width = 1024;
      canvas.height = 224;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('No se pudo preparar la vista previa.');
      ctx.fillStyle = '#14181c';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      const scale = Math.min(canvas.width / image.width, canvas.height / image.height);
      const width = image.width * scale;
      const height = image.height * scale;
      ctx.drawImage(image, (canvas.width - width) / 2, (canvas.height - height) / 2, width, height);
      return canvas.toDataURL('image/jpeg', 0.82);
    } finally { URL.revokeObjectURL(url); }
  }

  private finishClose(): void {
    if (this.ledPreview) {
      this.ledPreview.dispose();
      this.ledPreview = null;
      // A released WebGL context needs a fresh canvas when the editor opens again.
      const canvas = this.editorDialog.querySelector<HTMLCanvasElement>('#court-led-preview')!;
      canvas.replaceWith(canvas.cloneNode());
    }
    this.audioEditor.stopPreview();
    if (!this.opened) return;
    this.cancelKeyCapture();
    this.editorToken.value = '';
    this.opened = null;
    this.onOpenChange(false);
  }

  close(): void {
    this.cancelKeyCapture();
    if (this.opened?.open) this.opened.close();
    this.finishClose();
  }

  dispose(): void { this.close(); this.audioEditor.dispose(); this.events.abort(); }
}
